import React from 'react';
import { SalesLayout } from '@/components/SalesLayout';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { FormInput, FormDate, FormSelect, bookingTabs, VerticalTabNavigation } from '@/components/FormComponents';
import { Mail, MessageSquare, FileText } from 'lucide-react';
import {
  TemplateSelectionModal,
  NotificationPromptModal,
  BACKEND_URL,
  getAuthHeader,
  validatePAN,
  validateGST,
  validateAadhar,
  validateMobile,
  getInitialFormData,
} from '@/components/Bookings';


function BookingForm({ user, onLogout, mode = 'create' }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  
  const [loading, setLoading] = React.useState(mode === 'edit' || mode === 'view');
  const [activeTab, setActiveTab] = React.useState('customer');
  const [errors, setErrors] = React.useState({});
  
  // Template states for Registration notification
  const [templates, setTemplates] = React.useState([]);
  const [showTemplateModal, setShowTemplateModal] = React.useState(false);
  const [templateType, setTemplateType] = React.useState('email'); // 'email' or 'whatsapp'
  const [selectedTemplate, setSelectedTemplate] = React.useState(null);
  const [messagePreview, setMessagePreview] = React.useState('');
  
  // Chat-style notification prompt state
  const [showNotificationPrompt, setShowNotificationPrompt] = React.useState(false);
  const [notificationTrigger, setNotificationTrigger] = React.useState(''); // 'registration' or 'hsrp'
  const [originalValues, setOriginalValues] = React.useState({
    registration_number: '',
    hsrp_informed_date: ''
  });
  
  const [masters, setMasters] = React.useState({
    brands: [], models: [], variants: [], colors: [], rtos: [],
    salesReps: [], leadSources: [], addressProofs: [], financiers: [],
    accessories: [], insuranceCompanies: []
  });
  
  // Filtered cascading data for new vehicle
  const [filteredModels, setFilteredModels] = React.useState([]);
  const [filteredVariants, setFilteredVariants] = React.useState([]);
  const [filteredColors, setFilteredColors] = React.useState([]);
  
  // Filtered cascading data for exchange vehicle
  const [exFilteredModels, setExFilteredModels] = React.useState([]);
  const [exFilteredVariants, setExFilteredVariants] = React.useState([]);
  const [exFilteredColors, setExFilteredColors] = React.useState([]);
  
  const [formData, setFormData] = React.useState(getInitialFormData());

  const isEditMode = mode === 'edit';
  const isViewMode = mode === 'view';
  const isCreateMode = mode === 'create';
  const pageTitle = isViewMode ? 'View Booking' : (isEditMode ? 'Edit Booking' : 'New Booking');
  const submitButtonText = isEditMode ? 'Update Booking' : 'Create Booking';

  // First effect: Load masters data, then load booking if in edit/view mode
  React.useEffect(() => {
    const loadData = async () => {
      const mastersData = await fetchMasters();
      if ((isEditMode || isViewMode) && id && mastersData) {
        await fetchBooking(mastersData);
      }
    };
    loadData();
  }, [id, isEditMode, isViewMode]);

  const fetchMasters = async () => {
    try {
      const types = ['brand', 'model', 'variant', 'color', 'rto', 'sales_rep', 'lead_source', 'address_proof', 'financier', 'accessory', 'insurance_company'];
      const responses = await Promise.all(
        types.map(type => axios.get(`${BACKEND_URL}/api/masters?type=${type}`, getAuthHeader()))
      );
      
      const mastersData = {
        brands: responses[0].data,
        models: responses[1].data,
        variants: responses[2].data,
        colors: responses[3].data,
        rtos: responses[4].data,
        salesReps: responses[5].data,
        leadSources: responses[6].data,
        addressProofs: responses[7].data,
        financiers: responses[8].data,
        accessories: responses[9].data,
        insuranceCompanies: responses[10].data
      };
      setMasters(mastersData);
      return mastersData; // Return for immediate use
    } catch (error) {
      toast.error('Failed to fetch master data');
      return null;
    }
  };

  const fetchBooking = async (mastersData = null) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bookings/${id}`, getAuthHeader());
      const data = response.data;
      if (!data.accessories || typeof data.accessories !== 'object') {
        data.accessories = {};
      }
      setFormData(data);
      setLoading(false);
      
      // Store original values for comparison when editing
      setOriginalValues({
        registration_number: data.registration_number || '',
        hsrp_informed_date: data.hsrp_informed_date || ''
      });
      
      // Initialize cascading dropdowns for edit/view mode using parent_id hierarchy
      const brandsToUse = mastersData?.brands || masters.brands;
      
      // For new vehicle - cascade: brand -> model -> variant -> color using parent_id
      if (data.brand_name && brandsToUse.length > 0) {
        const brand = brandsToUse.find(b => b.name === data.brand_name);
        if (brand) {
          const modelsRes = await axios.get(`${BACKEND_URL}/api/masters?type=model&parent_id=${brand.id}`, getAuthHeader());
          setFilteredModels(modelsRes.data);
          
          if (data.model) {
            const model = modelsRes.data.find(m => m.name === data.model);
            if (model) {
              const variantsRes = await axios.get(`${BACKEND_URL}/api/masters?type=variant&parent_id=${model.id}`, getAuthHeader());
              setFilteredVariants(variantsRes.data);
              
              if (data.variant) {
                const variant = variantsRes.data.find(v => v.name === data.variant);
                if (variant) {
                  const colorsRes = await axios.get(`${BACKEND_URL}/api/masters?type=color&parent_id=${variant.id}`, getAuthHeader());
                  setFilteredColors(colorsRes.data);
                }
              }
            }
          }
        }
      }
      
      // For exchange vehicle - same parent_id cascading logic
      if (data.ex_brand_name && brandsToUse.length > 0) {
        const brand = brandsToUse.find(b => b.name === data.ex_brand_name);
        if (brand) {
          const modelsRes = await axios.get(`${BACKEND_URL}/api/masters?type=model&parent_id=${brand.id}`, getAuthHeader());
          setExFilteredModels(modelsRes.data);
          
          if (data.ex_model_name) {
            const model = modelsRes.data.find(m => m.name === data.ex_model_name);
            if (model) {
              const variantsRes = await axios.get(`${BACKEND_URL}/api/masters?type=variant&parent_id=${model.id}`, getAuthHeader());
              setExFilteredVariants(variantsRes.data);
              
              if (data.ex_variant_name) {
                const variant = variantsRes.data.find(v => v.name === data.ex_variant_name);
                if (variant) {
                  const colorsRes = await axios.get(`${BACKEND_URL}/api/masters?type=color&parent_id=${variant.id}`, getAuthHeader());
                  setExFilteredColors(colorsRes.data);
                }
              }
            }
          }
        }
      }
    } catch (error) {
      toast.error('Failed to fetch booking details');
      navigate('/sales/bookings');
    }
  };

  // Fetch templates for Email/WhatsApp
  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/templates`, getAuthHeader());
      setTemplates(response.data.filter(t => t.category === 'sales' || !t.category));
    } catch (error) {
      console.error('Failed to fetch templates');
    }
  };

  // Replace placeholders in template with booking data
  // iter-125: Schema-driven placeholder replacer.
  //
  // Previously this function was a hand-coded chain of ~17 string replaces and
  // had two silent bugs:
  //   • {{delivery_date}}  → mapped to formData.date_of_delivery (wrong field)
  //   • {{engine_no}}      → mapped to formData.engine_no       (non-existent)
  //
  // The Sales Templates UI (SalesTemplateManagement.js) advertises ~50
  // placeholders across 8 groups, but only ~17 ever resolved. The rest leaked
  // through as literal "{{placeholder}}" text in customer messages.
  //
  // This map is the single source of truth — every placeholder advertised in
  // SalesTemplateManagement.js has a getter here. Date fields run through
  // formatIndianDate() so they render as DD-MM-YYYY (matches the rest of the
  // app). Missing/null values render as empty string (safe — never leaks
  // "{{...}}" into the message).
  const formatIndianDate = (val) => {
    if (!val) return '';
    const s = String(val).trim();
    // ISO YYYY-MM-DD → DD-MM-YYYY
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (iso) return `${iso[3]}-${iso[2]}-${iso[1]}`;
    // Already DD-MM-YYYY or DD/MM/YYYY → normalise separator
    const dmy = /^(\d{2})[-/](\d{2})[-/](\d{4})/.exec(s);
    if (dmy) return `${dmy[1]}-${dmy[2]}-${dmy[3]}`;
    return s; // unknown shape — pass through unchanged
  };

  const fullCustomerName = () => {
    const fn = (formData.customer_first_name || '').trim();
    const ln = (formData.customer_last_name || '').trim();
    const sal = (formData.salutation || '').trim();
    const head = sal ? `${sal} ` : '';
    return `${head}${fn} ${ln}`.trim();
  };

  const fullAddress = () => {
    const parts = [
      formData.building_name, formData.door_no,
      formData.address_1, formData.address_2, formData.address_3,
      formData.area, formData.city, formData.state, formData.pin_code,
    ].map((p) => (p || '').trim()).filter(Boolean);
    return parts.join(', ');
  };

  // Map: placeholder name (without braces) → getter returning the resolved string
  const PLACEHOLDER_MAP = {
    // Customer
    customer_name: fullCustomerName,
    first_name: () => formData.customer_first_name || '',
    last_name: () => formData.customer_last_name || '',
    salutation: () => formData.salutation || '',
    mobile: () => formData.mobile_no_1 || '',
    whatsapp: () => formData.whatsapp_no_1 || formData.mobile_no_1 || '',
    email: () => formData.email || '',
    address: fullAddress,
    city: () => formData.city || '',
    aadhar_no: () => formData.aadhar_no || '',
    pan_no: () => formData.pan_no || '',
    // Service-aligned address fields (iter-121)
    building_name: () => formData.building_name || '',
    door_no: () => formData.door_no || '',
    address_1: () => formData.address_1 || '',
    address_2: () => formData.address_2 || '',
    address_3: () => formData.address_3 || '',
    area: () => formData.area || '',
    state: () => formData.state || '',
    pin_code: () => formData.pin_code || '',

    // Vehicle
    brand: () => formData.brand_name || '',
    model: () => formData.model || '',
    variant: () => formData.variant || '',
    color: () => formData.color || '',
    ex_showroom_price: () => formData.ex_showroom_price || '',
    on_road_price: () => formData.on_road_price || '',
    chassis_no: () => formData.chassis_no || '',
    engine_no: () => formData.engine_motor_no || '',
    battery_no: () => formData.battery_no || '',
    key_no: () => formData.key_no || '',

    // Booking
    booking_no: () => formData.booking_no || '',
    booking_date: () => formatIndianDate(formData.date_of_booking),
    delivery_date: () => formatIndianDate(formData.delivery_date || formData.date_of_delivery),
    sales_rep: () => formData.sales_representative || '',
    showroom: () => formData.showroom || '',
    dms_invoice_no: () => formData.dms_invoice_no || '',
    dms_invoice_date: () => formatIndianDate(formData.dms_invoice_date),

    // Registration & RTO
    registration_no: () => formData.registration_number || '',
    registration_date: () => formatIndianDate(formData.date_of_registration),
    rto_code: () => formData.rto_code || '',
    hsrp_number_plate: () => formData.hsrp_number_plate || '',

    // Finance
    financier_name: () => formData.financiers_name || '',
    loan_amount: () => formData.disbursement_amount || '',
    loan_status: () => formData.loan_status || '',
    finance_payment_status: () => formData.financiers_payment_status || '',

    // Insurance
    policy_no: () => formData.policy_no || '',
    insurance_type: () => formData.type_of_insurance || '',
    insurance_company_name: () => formData.insurance_company_name || '',
    premium: () => formData.gross_premium || formData.net_premium || '',
    od_expiry: () => formatIndianDate(formData.own_damage_expiry_date),
    own_damage_expiry_date: () => formatIndianDate(formData.own_damage_expiry_date),
    tp_expiry: () => formatIndianDate(formData.third_party_expiry_date),
    nominee_name: () => formData.insurance_nominee_name || '',

    // Company (sourced from logged-in user / company profile, not booking)
    company_name: () => user?.company_name || '',
    company_phone: () => user?.company_phone || '',
    company_address: () => user?.company_address || '',
  };

  const replacePlaceholders = (text) => {
    if (!text) return '';
    return String(text).replace(/\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g, (match, key) => {
      const getter = PLACEHOLDER_MAP[key];
      if (typeof getter !== 'function') return ''; // unknown placeholder → strip
      const value = getter();
      return value == null ? '' : String(value);
    });
  };

  // Open template modal
  const openTemplateModal = (type) => {
    setTemplateType(type);
    fetchTemplates();
    setSelectedTemplate(null);
    setMessagePreview('');
    setShowTemplateModal(true);
  };

  // Select template and show preview
  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    const preview = replacePlaceholders(template.body);
    setMessagePreview(preview);
  };

  // Send message via WhatsApp or Email
  const sendMessage = () => {
    if (!selectedTemplate) {
      toast.error('Please select a template');
      return;
    }

    const message = messagePreview;
    const phone = formData.mobile_no_1 || formData.whatsapp_no_1;
    const email = formData.email;

    if (templateType === 'whatsapp') {
      if (!phone) {
        toast.error('No mobile number available');
        return;
      }
      // Clean phone number and open WhatsApp
      const cleanPhone = phone.replace(/\D/g, '');
      const whatsappUrl = `https://web.whatsapp.com/send?phone=91${cleanPhone}&text=${encodeURIComponent(message)}`;
      { const _a = document.createElement('a'); _a.href = whatsappUrl; _a.target = '_blank'; _a.rel = 'noopener noreferrer'; document.body.appendChild(_a); _a.click(); document.body.removeChild(_a); }
      toast.success('WhatsApp opened with message');
    } else {
      if (!email) {
        toast.error('No email address available');
        return;
      }
      const subject = selectedTemplate.subject ? replacePlaceholders(selectedTemplate.subject) : 'Registration Update';
      const mailtoUrl = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
      window.open(mailtoUrl, '_blank');
      toast.success('Email client opened');
    }
    setShowTemplateModal(false);
    // Navigate back after sending (if triggered from update)
    if (notificationTrigger) {
      setTimeout(() => navigateBack(), 500);
    }
  };

  // Cascading fetch functions for vehicle dropdowns using parent_id hierarchy
  const fetchFilteredModels = async (brandId, isExchange = false) => {
    try {
      const url = brandId 
        ? `${BACKEND_URL}/api/masters?type=model&parent_id=${brandId}`
        : `${BACKEND_URL}/api/masters?type=model`;
      const response = await axios.get(url, getAuthHeader());
      if (isExchange) {
        setExFilteredModels(response.data);
        setExFilteredVariants([]);
        setExFilteredColors([]);
      } else {
        setFilteredModels(response.data);
        setFilteredVariants([]);
        setFilteredColors([]);
      }
    } catch (error) {
      console.error('Error fetching filtered models:', error);
    }
  };

  const fetchFilteredVariants = async (modelId, isExchange = false) => {
    try {
      const url = modelId 
        ? `${BACKEND_URL}/api/masters?type=variant&parent_id=${modelId}`
        : `${BACKEND_URL}/api/masters?type=variant`;
      const response = await axios.get(url, getAuthHeader());
      if (isExchange) {
        setExFilteredVariants(response.data);
        setExFilteredColors([]);
      } else {
        setFilteredVariants(response.data);
        setFilteredColors([]);
      }
    } catch (error) {
      console.error('Error fetching filtered variants:', error);
    }
  };

  const fetchFilteredColors = async (variantId, isExchange = false) => {
    try {
      const url = variantId 
        ? `${BACKEND_URL}/api/masters?type=color&parent_id=${variantId}`
        : `${BACKEND_URL}/api/masters?type=color`;
      const response = await axios.get(url, getAuthHeader());
      if (isExchange) {
        setExFilteredColors(response.data);
      } else {
        setFilteredColors(response.data);
      }
    } catch (error) {
      console.error('Error fetching filtered colors:', error);
    }
  };

  // Cascading handlers for new vehicle
  const handleBrandChange = async (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, brand_name: value, model: '', variant: '', color: '' }));
    const brand = masters.brands.find(b => b.name === value);
    if (brand) await fetchFilteredModels(brand.id, false);
  };

  const handleModelChange = async (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, model: value, variant: '', color: '' }));
    const model = filteredModels.find(m => m.name === value);
    if (model) await fetchFilteredVariants(model.id, false);
  };

  const handleVariantChange = async (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, variant: value, color: '' }));
    const variant = filteredVariants.find(v => v.name === value);
    if (variant) await fetchFilteredColors(variant.id, false);
  };

  // Cascading handlers for exchange vehicle
  const handleExBrandChange = async (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, ex_brand_name: value, ex_model_name: '', ex_variant_name: '', ex_color: '' }));
    const brand = masters.brands.find(b => b.name === value);
    if (brand) await fetchFilteredModels(brand.id, true);
  };

  const handleExModelChange = async (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, ex_model_name: value, ex_variant_name: '', ex_color: '' }));
    const model = exFilteredModels.find(m => m.name === value);
    if (model) await fetchFilteredVariants(model.id, true);
  };

  const handleExVariantChange = async (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, ex_variant_name: value, ex_color: '' }));
    const variant = exFilteredVariants.find(v => v.name === value);
    if (variant) await fetchFilteredColors(variant.id, true);
  };

  // Handle field change
  const handleChange = (name) => (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle numeric field change
  const handleNumericChange = (name) => (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle uppercase field change
  const handleUppercaseChange = (name) => (e) => {
    const value = e.target.value.toUpperCase();
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Indian currency helpers (Vendor Purchase Price)
  // - Editing: allow digits + one optional decimal (max 2 decimal places)
  // - Display on blur / view-mode: ₹1,23,456.78 (Indian grouping, always 2 decimals)
  const formatIndianCurrency = (val) => {
    if (val === '' || val === null || val === undefined) return '';
    const num = Number(val);
    if (Number.isNaN(num)) return '';
    return '₹' + num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const handleDecimalChange = (name) => (e) => {
    let value = e.target.value;
    // Strip everything except digits and a single dot
    value = value.replace(/[^\d.]/g, '');
    const firstDot = value.indexOf('.');
    if (firstDot !== -1) {
      value = value.slice(0, firstDot + 1) + value.slice(firstDot + 1).replace(/\./g, '');
      const [whole, dec] = value.split('.');
      value = whole + '.' + (dec || '').slice(0, 2);
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  const handleCurrencyBlur = (name) => () => {
    setFormData(prev => {
      const raw = prev[name];
      if (raw === '' || raw === null || raw === undefined) return prev;
      const num = Number(raw);
      if (Number.isNaN(num)) return prev;
      return { ...prev, [name]: num.toFixed(2) };
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.customer_first_name) newErrors.customer_first_name = 'First name is required';
    if (!formData.mobile_no_1) newErrors.mobile_no_1 = 'Mobile number is required';
    
    if (formData.mobile_no_1 && !validateMobile(formData.mobile_no_1)) {
      newErrors.mobile_no_1 = 'Invalid mobile number (10 digits starting with 6-9)';
    }
    if (formData.mobile_no_2 && !validateMobile(formData.mobile_no_2)) {
      newErrors.mobile_no_2 = 'Invalid mobile number';
    }
    if (formData.pan_no && !validatePAN(formData.pan_no)) {
      newErrors.pan_no = 'Invalid PAN format (e.g., ABCDE1234F)';
    }
    if (formData.gst_no && !validateGST(formData.gst_no)) {
      newErrors.gst_no = 'Invalid GST format (e.g., 22AAAAA0000A1Z5)';
    }
    if (formData.aadhar_no && !validateAadhar(formData.aadhar_no)) {
      newErrors.aadhar_no = 'Invalid Aadhar (must be 12 digits)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to navigate back
  const navigateBack = () => {
    if (returnTo) {
      navigate(decodeURIComponent(returnTo));
    } else {
      navigate('/sales/bookings');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix validation errors before submitting');
      return;
    }

    try {
      if (isEditMode) {
        await axios.put(`${BACKEND_URL}/api/bookings/${id}`, formData, getAuthHeader());
        toast.success('Booking updated successfully!');
        
        // Check if Registration Number or HSRP Informed Date was changed
        const regChanged = formData.registration_number && 
          formData.registration_number !== originalValues.registration_number;
        const hsrpChanged = formData.hsrp_informed_date && 
          formData.hsrp_informed_date !== originalValues.hsrp_informed_date;
        
        if (regChanged || hsrpChanged) {
          // Fetch templates before showing prompt
          await fetchTemplates();
          setNotificationTrigger(regChanged ? 'registration' : 'hsrp');
          setShowNotificationPrompt(true);
          return; // Don't navigate away yet
        }
      } else {
        await axios.post(`${BACKEND_URL}/api/bookings`, formData, getAuthHeader());
        toast.success('Booking created successfully!');
      }
      navigateBack();
    } catch (error) {
      toast.error(`Failed to ${isEditMode ? 'update' : 'create'} booking: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Handle notification prompt response
  const handleNotificationChoice = (choice) => {
    if (choice === 'whatsapp' || choice === 'both') {
      setTemplateType('whatsapp');
      setShowNotificationPrompt(false);
      setShowTemplateModal(true);
    } else if (choice === 'email') {
      setTemplateType('email');
      setShowNotificationPrompt(false);
      setShowTemplateModal(true);
    } else {
      // Skip - just navigate back
      setShowNotificationPrompt(false);
      navigateBack();
    }
  };

  // Close template modal and navigate back
  const handleTemplateModalClose = () => {
    setShowTemplateModal(false);
    navigateBack();
  };

  const handleAccessoryChange = (accessoryName, checked, price) => {
    setFormData(prev => {
      const newAccessories = { ...prev.accessories };
      if (checked) {
        newAccessories[accessoryName] = price || 0;
      } else {
        delete newAccessories[accessoryName];
      }
      const total = Object.values(newAccessories).reduce((sum, p) => sum + (p || 0), 0);
      return { ...prev, accessories: newAccessories, accessories_total: total };
    });
  };

  if (loading) {
    return (
      <SalesLayout user={user} onLogout={onLogout} title={pageTitle}>
        <div className="bg-white p-6 rounded-lg border">
          <p>Loading booking details...</p>
        </div>
      </SalesLayout>
    );
  }

  return (
    <SalesLayout user={user} onLogout={onLogout} title={pageTitle}>
      <div className="bg-white rounded-lg border shadow-sm">
        {/* Booking Info Header - Show for Edit/View/Delete modes */}
        {(isEditMode || isViewMode) && formData.booking_no && (
          <div className="text-white p-4 rounded-t-2xl" style={{ background: 'linear-gradient(135deg, #4338CA 0%, #4F46E5 50%, #6D28D9 100%)' }}>
            <div className="flex flex-wrap gap-6 items-center">
              <div>
                <span className="text-sm text-blue-200">Booking No:</span>
                <p className="text-2xl font-bold">{formData.booking_no}</p>
              </div>
              <div>
                <span className="text-sm text-blue-200">Customer Name:</span>
                <p className="text-xl font-semibold">
                  {formData.salutation} {formData.customer_first_name} {formData.customer_last_name}
                </p>
              </div>
              <div>
                <span className="text-sm text-blue-200">Registration No:</span>
                <p className="text-xl font-semibold">
                  {formData.registration_number || 'Not Registered'}
                </p>
              </div>
              {isViewMode && (
                <div className="ml-auto">
                  <span className="bg-amber-400 text-amber-900 px-3 py-1 rounded-full text-sm font-semibold">
                    View Only Mode
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Main Content - Tally Style Layout with Tabs on Right */}
        <div className="flex">
          {/* Left: Form Fields */}
          <div className="flex-1 p-5 border-r min-h-[600px]">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">{pageTitle}</h2>
              <div className="text-sm text-gray-500">
                Current Tab: <span className="font-semibold text-blue-600">{bookingTabs.find(t => t.id === activeTab)?.label}</span>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Customer Tab */}
              {activeTab === 'customer' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <FormSelect label="Salutation" name="salutation" value={formData.salutation} onChange={handleChange('salutation')} options={['Mr', 'Mrs', 'Ms', 'M/s']} disabled={isViewMode} />
                  <FormInput label="First Name *" name="customer_first_name" value={formData.customer_first_name} onChange={handleChange('customer_first_name')} error={errors.customer_first_name} disabled={isViewMode} />
                  <FormInput label="Last Name" name="customer_last_name" value={formData.customer_last_name} onChange={handleChange('customer_last_name')} disabled={isViewMode} />
                  <FormInput label="Father/Husband Name" name="father_husband_name" value={formData.father_husband_name} onChange={handleChange('father_husband_name')} disabled={isViewMode} />
                  <FormDate label="Date of Birth" name="dob" value={formData.dob} onChange={handleChange('dob')} disabled={isViewMode} />
                  <FormSelect label="Gender" name="gender" value={formData.gender} onChange={handleChange('gender')} options={['Male', 'Female', 'Other']} disabled={isViewMode} />
                  <FormInput label="Mobile No. 1 *" name="mobile_no_1" value={formData.mobile_no_1} onChange={handleNumericChange('mobile_no_1')} maxLength={10} error={errors.mobile_no_1} disabled={isViewMode} />
                  <FormInput label="Mobile No. 2" name="mobile_no_2" value={formData.mobile_no_2} onChange={handleNumericChange('mobile_no_2')} maxLength={10} error={errors.mobile_no_2} disabled={isViewMode} />
                  <FormInput label="WhatsApp No." name="whatsapp_no_1" value={formData.whatsapp_no_1} onChange={handleNumericChange('whatsapp_no_1')} maxLength={10} disabled={isViewMode} />
                  <FormInput label="Email" name="email" value={formData.email} onChange={handleChange('email')} type="email" disabled={isViewMode} />
                  <FormInput label="Building Name" name="building_name" value={formData.building_name} onChange={handleChange('building_name')} disabled={isViewMode} />
                  <FormInput label="Door No." name="door_no" value={formData.door_no} onChange={handleChange('door_no')} disabled={isViewMode} />
                  <FormInput label="Address Line 1" name="address_1" value={formData.address_1} onChange={handleChange('address_1')} disabled={isViewMode} />
                  <FormInput label="Address Line 2" name="address_2" value={formData.address_2} onChange={handleChange('address_2')} disabled={isViewMode} />
                  {/* iter-121: Address Line 3 — newly added to surface a 3rd address slot for long postal addresses */}
                  <FormInput label="Address Line 3" name="address_3" value={formData.address_3} onChange={handleChange('address_3')} disabled={isViewMode} />
                  <FormInput label="Area" name="area" value={formData.area} onChange={handleChange('area')} disabled={isViewMode} />
                  <FormInput label="City" name="city" value={formData.city} onChange={handleChange('city')} disabled={isViewMode} />
                  <FormInput label="State" name="state" value={formData.state} onChange={handleChange('state')} disabled={isViewMode} />
                  <FormInput label="Pin Code" name="pin_code" value={formData.pin_code} onChange={handleNumericChange('pin_code')} maxLength={6} disabled={isViewMode} />
                  <FormInput label="Aadhar No." name="aadhar_no" value={formData.aadhar_no} onChange={handleNumericChange('aadhar_no')} maxLength={12} error={errors.aadhar_no} disabled={isViewMode} />
                  <FormInput label="PAN No." name="pan_no" value={formData.pan_no} onChange={handleUppercaseChange('pan_no')} maxLength={10} error={errors.pan_no} disabled={isViewMode} />
                  <FormInput label="GST No." name="gst_no" value={formData.gst_no} onChange={handleUppercaseChange('gst_no')} maxLength={15} error={errors.gst_no} disabled={isViewMode} />
                </div>
              )}

          {/* Vehicle Tab */}
          {activeTab === 'vehicle' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormSelect label="Vehicle Selection" name="vehicle_selection" value={formData.vehicle_selection} onChange={handleChange('vehicle_selection')} options={['New', 'Pre-Owned']} />
              <FormSelect label="Vehicle Type" name="vehicle_type" value={formData.vehicle_type} onChange={handleChange('vehicle_type')} options={['4 wheeler', '3 wheeler', '2 wheeler']} />
              <FormSelect label="Usage" name="usage" value={formData.usage} onChange={handleChange('usage')} options={['Personal', 'Commercial']} />
              <div>
                <label className="block text-sm font-medium mb-1">Brand</label>
                <select className="w-full p-2 border rounded" value={formData.brand_name} onChange={handleBrandChange} data-testid="vehicle-brand-select">
                  <option value="">Select Brand</option>
                  {masters.brands.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Model</label>
                <select 
                  className={`w-full p-2 border rounded ${!formData.brand_name ? 'bg-gray-100 cursor-not-allowed' : ''}`} 
                  value={formData.model} 
                  onChange={handleModelChange}
                  disabled={!formData.brand_name}
                  data-testid="vehicle-model-select"
                >
                  <option value="">{formData.brand_name ? 'Select Model' : 'Select Brand first'}</option>
                  {/* Include saved value if not in filtered list */}
                  {formData.model && !filteredModels.some(m => m.name === formData.model) && (
                    <option value={formData.model}>{formData.model} (saved)</option>
                  )}
                  {filteredModels.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Variant</label>
                <select 
                  className={`w-full p-2 border rounded ${!formData.model ? 'bg-gray-100 cursor-not-allowed' : ''}`} 
                  value={formData.variant} 
                  onChange={handleVariantChange}
                  disabled={!formData.model}
                  data-testid="vehicle-variant-select"
                >
                  <option value="">{formData.model ? 'Select Variant' : 'Select Model first'}</option>
                  {/* Include saved value if not in filtered list */}
                  {formData.variant && !filteredVariants.some(v => v.name === formData.variant) && (
                    <option value={formData.variant}>{formData.variant} (saved)</option>
                  )}
                  {filteredVariants.map(v => <option key={v.id} value={v.name}>{v.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Color</label>
                <select 
                  className={`w-full p-2 border rounded ${!formData.variant ? 'bg-gray-100 cursor-not-allowed' : ''}`} 
                  value={formData.color} 
                  onChange={handleChange('color')}
                  disabled={!formData.variant}
                  data-testid="vehicle-color-select"
                >
                  <option value="">{formData.variant ? 'Select Color' : 'Select Variant first'}</option>
                  {/* Include saved value if not in filtered list */}
                  {formData.color && !filteredColors.some(c => c.name === formData.color) && (
                    <option value={formData.color}>{formData.color} (saved)</option>
                  )}
                  {filteredColors.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <FormSelect label="Fuel Type" name="fuel_type" value={formData.fuel_type} onChange={handleChange('fuel_type')} options={['Petrol', 'Diesel', 'CNG', 'Electric', 'Hybrid']} />
              <FormInput label="Chassis No." name="chassis_no" value={formData.chassis_no} onChange={handleUppercaseChange('chassis_no')} />
              <FormInput label="Engine/Motor No." name="engine_motor_no" value={formData.engine_motor_no} onChange={handleUppercaseChange('engine_motor_no')} />
              <FormInput label="Key No." name="key_no" value={formData.key_no} onChange={handleChange('key_no')} />
              <FormInput label="Charger No." name="charger_no" value={formData.charger_no} onChange={handleChange('charger_no')} />
              <FormInput label="Battery No." name="battery_no" value={formData.battery_no} onChange={handleChange('battery_no')} />
              <FormInput label="Controller No." name="controller_no" value={formData.controller_no} onChange={handleChange('controller_no')} />
            </div>
          )}

          {/* Insurance Tab */}
          {activeTab === 'insurance' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Insurance Company</label>
                <select className="w-full p-2 border rounded" value={formData.insurance_company_name || ''} onChange={handleChange('insurance_company_name')}>
                  <option value="">Select Company</option>
                  {masters.insuranceCompanies.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <FormSelect label="Type of Insurance" name="type_of_insurance" value={formData.type_of_insurance} onChange={handleChange('type_of_insurance')} options={['comprehensive', 'stand alone', '3rd party', 'individual']} />
              <FormInput label="Policy No." name="policy_no" value={formData.policy_no} onChange={handleChange('policy_no')} />
              <FormSelect label="Policy Status" name="policy_status" value={formData.policy_status} onChange={handleChange('policy_status')} options={['Received', 'Not Received']} />
              <FormInput label="Gross Premium (₹)" name="gross_premium" value={formData.gross_premium} onChange={handleChange('gross_premium')} type="number" />
              <FormInput label="Net Premium (₹)" name="net_premium" value={formData.net_premium} onChange={handleChange('net_premium')} type="number" />
              <FormDate label="Own Damage Expiry Date" name="own_damage_expiry_date" value={formData.own_damage_expiry_date} onChange={handleChange('own_damage_expiry_date')} />
              <FormDate label="Third Party Expiry Date" name="third_party_expiry_date" value={formData.third_party_expiry_date} onChange={handleChange('third_party_expiry_date')} />
              <FormInput label="Nominee Name" name="insurance_nominee_name" value={formData.insurance_nominee_name} onChange={handleChange('insurance_nominee_name')} />
              <FormSelect label="Relationship" name="relationship" value={formData.relationship} onChange={handleChange('relationship')} options={['son', 'daughter', 'wife', 'husband', 'sister', 'brother', 'father', 'mother']} />
              <FormDate label="Nominee DOB" name="nominee_dob" value={formData.nominee_dob} onChange={handleChange('nominee_dob')} />
            </div>
          )}

          {/* Registration Tab */}
          {activeTab === 'registration' && (
            <div className="space-y-4">
              {/* Notification Section - shown when Registration No and Delivery Date are filled */}
              {(formData.registration_number || formData.date_of_delivery) && (isEditMode || isViewMode) && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <h4 className="font-semibold text-green-800">Registration Update Ready</h4>
                      <p className="text-sm text-green-600">
                        {formData.registration_number && `Reg: ${formData.registration_number}`}
                        {formData.registration_number && formData.date_of_delivery && ' | '}
                        {formData.date_of_delivery && `Delivery: ${formData.date_of_delivery}`}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => openTemplateModal('whatsapp')}
                        className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                        data-testid="send-whatsapp-btn"
                      >
                        <MessageSquare className="w-4 h-4" />
                        <span>WhatsApp</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => openTemplateModal('email')}
                        className="flex items-center gap-2 px-4 py-2 text-white rounded-xl shadow-[0_4px_12px_rgba(79,70,229,0.30)] hover:shadow-[0_8px_20px_rgba(79,70,229,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95"
                        style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)' }}
                        data-testid="send-email-btn"
                      >
                        <Mail className="w-4 h-4" />
                        <span>Email</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">RTO Area</label>
                  <select className="w-full p-2 border rounded" value={formData.rto_area || ''} onChange={(e) => {
                    const selected = masters.rtos.find(r => r.name === e.target.value);
                    setFormData(prev => ({
                      ...prev,
                      rto_area: e.target.value,
                      rto_code: selected?.code || '',
                      rto_broker_name: selected?.name || '',
                      rto_mobile_no: selected?.mobile || ''
                    }));
                  }} disabled={isViewMode}>
                    <option value="">Select RTO</option>
                    {masters.rtos.map(r => <option key={r.id} value={r.name}>{r.name} ({r.code})</option>)}
                  </select>
                </div>
                <FormInput label="RTO Code" name="rto_code" value={formData.rto_code} onChange={handleChange('rto_code')} />
                <FormInput label="RTO Broker Name" name="rto_broker_name" value={formData.rto_broker_name} onChange={handleChange('rto_broker_name')} />
                <FormInput label="RTO Mobile No." name="rto_mobile_no" value={formData.rto_mobile_no} onChange={handleNumericChange('rto_mobile_no')} maxLength={10} />
                <FormSelect label="RTO Paper Status" name="rto_paper_status" value={formData.rto_paper_status} onChange={handleChange('rto_paper_status')} options={['pending', 'submitted', 'completed']} />
                <FormInput label="Registration Number" name="registration_number" value={formData.registration_number} onChange={handleUppercaseChange('registration_number')} />
                <FormDate label="Date of Registration" name="date_of_registration" value={formData.date_of_registration} onChange={handleChange('date_of_registration')} />
                <FormSelect label="RC Status" name="rc_status" value={formData.rc_status} onChange={handleChange('rc_status')} options={['pending', 'applied', 'received']} />
                <FormSelect label="HSRP Number Plate" name="hsrp_number_plate" value={formData.hsrp_number_plate} onChange={handleChange('hsrp_number_plate')} options={['pending', 'applied', 'received']} />
                <FormSelect label="HSRP Received Informed" name="hsrp_received_informed" value={formData.hsrp_received_informed} onChange={handleChange('hsrp_received_informed')} options={['Yes', 'No']} />
                <FormDate label="HSRP Informed Date" name="hsrp_informed_date" value={formData.hsrp_informed_date} onChange={handleChange('hsrp_informed_date')} />
              </div>
            </div>
          )}

          {/* Accessories Tab */}
          {activeTab === 'accessories' && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {masters.accessories.map(acc => (
                  <div key={acc.id} className="flex items-center gap-2 p-2 border rounded">
                    <input
                      type="checkbox"
                      checked={!!formData.accessories?.[acc.name]}
                      onChange={(e) => handleAccessoryChange(acc.name, e.target.checked, acc.price)}
                      className="w-4 h-4"
                    />
                    <span className="flex-1">{acc.name}</span>
                    <span className="text-sm text-gray-600">₹{acc.price || 0}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-4 bg-gray-50 rounded">
                <span className="font-semibold">Total: ₹{formData.accessories_total || 0}</span>
              </div>
            </div>
          )}

          {/* Accounting Tab */}
          {activeTab === 'accounting' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormSelect label="Invoice DMS" name="invoice_dms" value={formData.invoice_dms} onChange={handleChange('invoice_dms')} options={['', 'Yes', 'No']} />
              <FormInput label="DMS Invoice No." name="dms_invoice_no" value={formData.dms_invoice_no} onChange={handleChange('dms_invoice_no')} />
              <FormDate label="DMS Invoice Date" name="dms_invoice_date" value={formData.dms_invoice_date} onChange={handleChange('dms_invoice_date')} />
              <FormSelect label="Tally Invoice" name="tally_invoice" value={formData.tally_invoice} onChange={handleChange('tally_invoice')} options={['', 'Yes', 'No']} />
              <FormInput label="Customer Balance (₹)" name="customer_balance" value={formData.customer_balance} onChange={handleChange('customer_balance')} type="number" />
              <FormSelect label="Ledger Status" name="ledger_status" value={formData.ledger_status} onChange={handleChange('ledger_status')} options={['pending', 'verified', 'cleared']} />
              <FormInput label="Accounts Verified By" name="accounts_verified_by" value={formData.accounts_verified_by} onChange={handleChange('accounts_verified_by')} />
              <FormSelect label="Financiers Payment Status" name="financiers_payment_status" value={formData.financiers_payment_status} onChange={handleChange('financiers_payment_status')} options={['pending', 'partial', 'received']} />
              <FormInput label="Pool Amount (₹)" name="pool_amount" value={formData.pool_amount} onChange={handleChange('pool_amount')} type="number" />
              <FormInput label="Scheme Discount 1 (₹)" name="scheme_discount_1" value={formData.scheme_discount_1} onChange={handleChange('scheme_discount_1')} type="number" />
              <FormInput label="Scheme Discount 2 (₹)" name="scheme_discount_2" value={formData.scheme_discount_2} onChange={handleChange('scheme_discount_2')} type="number" />
              <FormInput label="Special Discount (₹)" name="special_discount" value={formData.special_discount} onChange={handleChange('special_discount')} type="number" />
            </div>
          )}

          {/* Exchange Tab */}
          {activeTab === 'exchange' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput label="Exchange Vehicle No." name="exchange_vehicle_no" value={formData.exchange_vehicle_no} onChange={handleUppercaseChange('exchange_vehicle_no')} />
              <div>
                <label className="block text-sm font-medium mb-1">Brand</label>
                <select className="w-full p-2 border rounded" value={formData.ex_brand_name} onChange={handleExBrandChange} data-testid="exchange-brand-select">
                  <option value="">Select Brand</option>
                  {masters.brands.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Model</label>
                <select 
                  className={`w-full p-2 border rounded ${!formData.ex_brand_name ? 'bg-gray-100 cursor-not-allowed' : ''}`} 
                  value={formData.ex_model_name} 
                  onChange={handleExModelChange}
                  disabled={!formData.ex_brand_name}
                  data-testid="exchange-model-select"
                >
                  <option value="">{formData.ex_brand_name ? 'Select Model' : 'Select Brand first'}</option>
                  {/* Include saved value if not in filtered list */}
                  {formData.ex_model_name && !exFilteredModels.some(m => m.name === formData.ex_model_name) && (
                    <option value={formData.ex_model_name}>{formData.ex_model_name} (saved)</option>
                  )}
                  {exFilteredModels.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Variant</label>
                <select 
                  className={`w-full p-2 border rounded ${!formData.ex_model_name ? 'bg-gray-100 cursor-not-allowed' : ''}`} 
                  value={formData.ex_variant_name || ''} 
                  onChange={handleExVariantChange}
                  disabled={!formData.ex_model_name}
                  data-testid="exchange-variant-select"
                >
                  <option value="">{formData.ex_model_name ? 'Select Variant' : 'Select Model first'}</option>
                  {/* Include saved value if not in filtered list */}
                  {formData.ex_variant_name && !exFilteredVariants.some(v => v.name === formData.ex_variant_name) && (
                    <option value={formData.ex_variant_name}>{formData.ex_variant_name} (saved)</option>
                  )}
                  {exFilteredVariants.map(v => <option key={v.id} value={v.name}>{v.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Color</label>
                <select 
                  className={`w-full p-2 border rounded ${!formData.ex_variant_name ? 'bg-gray-100 cursor-not-allowed' : ''}`} 
                  value={formData.ex_color} 
                  onChange={handleChange('ex_color')}
                  disabled={!formData.ex_variant_name}
                  data-testid="exchange-color-select"
                >
                  <option value="">{formData.ex_variant_name ? 'Select Color' : 'Select Variant first'}</option>
                  {/* Include saved value if not in filtered list */}
                  {formData.ex_color && !exFilteredColors.some(c => c.name === formData.ex_color) && (
                    <option value={formData.ex_color}>{formData.ex_color} (saved)</option>
                  )}
                  {exFilteredColors.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
              </div>
              <FormInput label="Year of Manufacturing" name="year_of_manufacturing" value={formData.year_of_manufacturing} onChange={handleNumericChange('year_of_manufacturing')} maxLength={4} />
              <FormInput label="Exchange Value (₹)" name="exchange_value" value={formData.exchange_value} onChange={handleChange('exchange_value')} type="number" />
              <FormSelect label="Inwards Status" name="inwards_status" value={formData.inwards_status} onChange={handleChange('inwards_status')} options={['Inwards Pending', 'Inwards Done', 'Delivered']} />
              <FormDate label="Date of Delivery" name="date_of_delivery" value={formData.date_of_delivery} onChange={handleChange('date_of_delivery')} />
              <FormInput label="Traffic Challans Pending" name="traffic_challans_pending" value={formData.traffic_challans_pending} onChange={handleChange('traffic_challans_pending')} type="number" />
            </div>
          )}

          {/* Booking Tab */}
          {activeTab === 'booking' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormSelect label="Booking Through" name="booking_through" value={formData.booking_through} onChange={handleChange('booking_through')} options={['Direct', 'Agent', 'Reference', 'Online']} />
              <FormDate label="Date of Booking" name="date_of_booking" value={formData.date_of_booking} onChange={handleChange('date_of_booking')} />
              <div>
                <label className="block text-sm font-medium mb-1">Sales Representative</label>
                <select className="w-full p-2 border rounded" value={formData.sales_representative || ''} onChange={handleChange('sales_representative')}>
                  <option value="">Select Sales Rep</option>
                  {masters.salesReps.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                </select>
              </div>
              <FormDate label="Delivery Date" name="delivery_date" value={formData.delivery_date} onChange={handleChange('delivery_date')} />
              <div>
                <label className="block text-sm font-medium mb-1">Lead Source</label>
                <select className="w-full p-2 border rounded" value={formData.lead_source || ''} onChange={handleChange('lead_source')}>
                  <option value="">Select Lead Source</option>
                  {masters.leadSources.map(l => <option key={l.id} value={l.name}>{l.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Address Proof</label>
                <select className="w-full p-2 border rounded" value={formData.address_proof || ''} onChange={handleChange('address_proof')} disabled={isViewMode}>
                  <option value="">Select Address Proof</option>
                  {masters.addressProofs.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
                </select>
              </div>
              <FormSelect label="Address Proof Received" name="address_proof_received" value={formData.address_proof_received} onChange={handleChange('address_proof_received')} options={['Pending', 'Received']} disabled={isViewMode} />
              <FormSelect label="Photos Received" name="photos" value={formData.photos} onChange={handleChange('photos')} options={['Pending', 'Received']} disabled={isViewMode} />
            </div>
          )}

          {/* Finance Tab */}
          {activeTab === 'finance' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Financier</label>
                <select className="w-full p-2 border rounded" value={formData.financiers_name || ''} onChange={(e) => {
                  const selected = masters.financiers.find(f => f.name === e.target.value);
                  setFormData(prev => ({
                    ...prev,
                    financiers_name: e.target.value,
                    financiers_address_1: selected?.address_1 || '',
                    financiers_city: selected?.city || '',
                    financiers_state: selected?.state || '',
                    financiers_pincode: selected?.pincode || '',
                    financiers_mobile_no: selected?.mobile || ''
                  }));
                }}>
                  <option value="">Select Financier</option>
                  {masters.financiers.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
                </select>
              </div>
              <FormInput label="Financier Address 1" name="financiers_address_1" value={formData.financiers_address_1} onChange={handleChange('financiers_address_1')} />
              <FormInput label="Financier Address 2" name="financiers_address_2" value={formData.financiers_address_2} onChange={handleChange('financiers_address_2')} />
              <FormInput label="Financier Area" name="financiers_area" value={formData.financiers_area} onChange={handleChange('financiers_area')} />
              <FormInput label="Financier City" name="financiers_city" value={formData.financiers_city} onChange={handleChange('financiers_city')} />
              <FormInput label="Financier State" name="financiers_state" value={formData.financiers_state} onChange={handleChange('financiers_state')} />
              <FormInput label="Financier Pincode" name="financiers_pincode" value={formData.financiers_pincode} onChange={handleNumericChange('financiers_pincode')} maxLength={6} />
              <FormInput label="Financier Mobile" name="financiers_mobile_no" value={formData.financiers_mobile_no} onChange={handleNumericChange('financiers_mobile_no')} maxLength={10} />
              <FormDate label="Date of Disbursement" name="date_of_disbursement" value={formData.date_of_disbursement} onChange={handleChange('date_of_disbursement')} />
              <FormInput label="Disbursement Amount (₹)" name="disbursement_amount" value={formData.disbursement_amount} onChange={handleChange('disbursement_amount')} type="number" />
              <FormSelect label="Loan Status" name="loan_status" value={formData.loan_status} onChange={handleChange('loan_status')} options={['pending', 'approved', 'disbursed', 'rejected']} />
              <FormInput label="Loan Account No." name="loan_account_no" value={formData.loan_account_no} onChange={handleChange('loan_account_no')} />
              <FormInput label="EMI Amount (₹)" name="emi_amount" value={formData.emi_amount} onChange={handleChange('emi_amount')} type="number" />
              <FormInput label="Tenure (Months)" name="tenure_months" value={formData.tenure_months} onChange={handleChange('tenure_months')} type="number" />
              <FormInput label="Rate of Interest (%)" name="rate_of_interest" value={formData.rate_of_interest} onChange={handleChange('rate_of_interest')} type="number" step="0.01" />
            </div>
          )}

          {/* PME Drive Tab */}
          {activeTab === 'pme' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput label="PME Drive No." name="pme_drive_no" value={formData.pme_drive_no} onChange={handleChange('pme_drive_no')} />
              <FormDate label="PME Submission Date" name="pme_submission_date" value={formData.pme_submission_date} onChange={handleChange('pme_submission_date')} />
              <FormSelect label="PME Status" name="pme_status" value={formData.pme_status} onChange={handleChange('pme_status')} options={['pending', 'submitted', 'approved', 'rejected']} />
              <FormInput label="PME Remarks" name="pme_remarks" value={formData.pme_remarks} onChange={handleChange('pme_remarks')} />
            </div>
          )}

          {/* Sellers Tab */}
          {activeTab === 'sellers' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput label="Seller Name" name="sellers_name" value={formData.sellers_name} onChange={handleChange('sellers_name')} />
              <FormInput label="Father Name" name="sellers_father_name" value={formData.sellers_father_name} onChange={handleChange('sellers_father_name')} />
              <FormInput label="Phone 1" name="sellers_phone_1" value={formData.sellers_phone_1} onChange={handleNumericChange('sellers_phone_1')} maxLength={10} />
              <FormInput label="Phone 2" name="sellers_phone_2" value={formData.sellers_phone_2} onChange={handleNumericChange('sellers_phone_2')} maxLength={10} />
              <FormInput label="Email" name="sellers_email" value={formData.sellers_email} onChange={handleChange('sellers_email')} type="email" />
              <FormInput label="Address 1" name="sellers_address_1" value={formData.sellers_address_1} onChange={handleChange('sellers_address_1')} />
              <FormInput label="Address 2" name="sellers_address_2" value={formData.sellers_address_2} onChange={handleChange('sellers_address_2')} />
              <FormInput label="Area" name="sellers_area" value={formData.sellers_area} onChange={handleChange('sellers_area')} />
              <FormInput label="City" name="sellers_city" value={formData.sellers_city} onChange={handleChange('sellers_city')} />
              <FormInput label="State" name="sellers_state" value={formData.sellers_state} onChange={handleChange('sellers_state')} />
              <FormInput label="Pincode" name="sellers_pincode" value={formData.sellers_pincode} onChange={handleNumericChange('sellers_pincode')} maxLength={6} />
              <FormInput label="Aadhar No." name="sellers_aadhar" value={formData.sellers_aadhar} onChange={handleNumericChange('sellers_aadhar')} maxLength={12} />
              <FormInput label="PAN No." name="sellers_pan" value={formData.sellers_pan} onChange={handleUppercaseChange('sellers_pan')} maxLength={10} />
            </div>
          )}

          {/* Agents Tab */}
          {activeTab === 'agents' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput label="Agent Name" name="agents_name" value={formData.agents_name} onChange={handleChange('agents_name')} />
              <FormInput label="Father Name" name="agents_father_name" value={formData.agents_father_name} onChange={handleChange('agents_father_name')} />
              <FormInput label="Phone 1" name="agents_phone_1" value={formData.agents_phone_1} onChange={handleNumericChange('agents_phone_1')} maxLength={10} />
              <FormInput label="Phone 2" name="agents_phone_2" value={formData.agents_phone_2} onChange={handleNumericChange('agents_phone_2')} maxLength={10} />
              <FormInput label="Email" name="agents_email" value={formData.agents_email} onChange={handleChange('agents_email')} type="email" />
              <FormInput label="Address 1" name="agents_address_1" value={formData.agents_address_1} onChange={handleChange('agents_address_1')} />
              <FormInput label="Address 2" name="agents_address_2" value={formData.agents_address_2} onChange={handleChange('agents_address_2')} />
              <FormInput label="Area" name="agents_area" value={formData.agents_area} onChange={handleChange('agents_area')} />
              <FormInput label="City" name="agents_city" value={formData.agents_city} onChange={handleChange('agents_city')} />
              <FormInput label="State" name="agents_state" value={formData.agents_state} onChange={handleChange('agents_state')} />
              <FormInput label="Pincode" name="agents_pincode" value={formData.agents_pincode} onChange={handleNumericChange('agents_pincode')} maxLength={6} />
              <FormInput label="Commission (₹)" name="agents_commission" value={formData.agents_commission} onChange={handleChange('agents_commission')} type="number" />
            </div>
          )}

          {/* Vendors Tab */}
          {activeTab === 'vendors' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <FormInput label="Vendor Name" name="vendors_name" value={formData.vendors_name} onChange={handleChange('vendors_name')} disabled={isViewMode} />
              <FormInput label="Phone 1" name="vendors_phone_1" value={formData.vendors_phone_1} onChange={handleNumericChange('vendors_phone_1')} maxLength={10} disabled={isViewMode} />
              <FormInput label="Phone 2" name="vendors_phone_2" value={formData.vendors_phone_2} onChange={handleNumericChange('vendors_phone_2')} maxLength={10} disabled={isViewMode} />
              <FormInput label="Email" name="vendors_email" value={formData.vendors_email} onChange={handleChange('vendors_email')} type="email" disabled={isViewMode} />
              <FormInput label="Address 1" name="vendors_address_1" value={formData.vendors_address_1} onChange={handleChange('vendors_address_1')} disabled={isViewMode} />
              <FormInput label="Address 2" name="vendors_address_2" value={formData.vendors_address_2} onChange={handleChange('vendors_address_2')} disabled={isViewMode} />
              <FormInput label="Area" name="vendors_area" value={formData.vendors_area} onChange={handleChange('vendors_area')} disabled={isViewMode} />
              <FormInput label="City" name="vendors_city" value={formData.vendors_city} onChange={handleChange('vendors_city')} disabled={isViewMode} />
              <FormInput label="State" name="vendors_state" value={formData.vendors_state} onChange={handleChange('vendors_state')} disabled={isViewMode} />
              <FormInput label="Pincode" name="vendors_pincode" value={formData.vendors_pincode} onChange={handleNumericChange('vendors_pincode')} maxLength={6} disabled={isViewMode} />
              <FormInput label="GST No." name="vendors_gst" value={formData.vendors_gst} onChange={handleUppercaseChange('vendors_gst')} maxLength={15} disabled={isViewMode} />
              <FormInput
                label="Purchase Price"
                name="vendors_purchase_price"
                value={isViewMode ? formatIndianCurrency(formData.vendors_purchase_price) : (formData.vendors_purchase_price ?? '')}
                onChange={handleDecimalChange('vendors_purchase_price')}
                onBlur={handleCurrencyBlur('vendors_purchase_price')}
                placeholder="0.00"
                inputMode="decimal"
                disabled={isViewMode}
                data-testid="vendor-purchase-price-input"
              />
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex gap-4 pt-6 mt-6 border-t">
            {/* Generate Proforma Invoice Button */}
            {(mode === 'edit' || mode === 'view') && formData.brand_name && (
              <button
                type="button"
                onClick={() => navigate(`/sales/proforma-invoice/booking/${id}`)}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium shadow-sm transition-colors flex items-center gap-2"
                data-testid="generate-proforma-booking-btn"
              >
                <FileText className="w-4 h-4" />
                Generate Proforma
              </button>
            )}
            {!isViewMode && (
              <button type="submit" data-testid="submit-booking-btn" className="px-6 py-2 text-white rounded-xl font-medium shadow-[0_4px_12px_rgba(79,70,229,0.30)] hover:shadow-[0_8px_20px_rgba(79,70,229,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95" style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)' }}>
                {submitButtonText}
              </button>
            )}
            {isViewMode && (
              <button 
                type="button" 
                onClick={() => navigate(`/sales/bookings/${id}/edit`)} 
                data-testid="edit-booking-btn"
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium shadow-sm transition-colors"
              >
                Edit Booking
              </button>
            )}
            <button type="button" onClick={navigateBack} data-testid="cancel-btn" className="px-6 py-2 border rounded-lg hover:bg-gray-100 font-medium transition-colors">
              {isViewMode ? 'Back to List' : 'Cancel'}
            </button>
          </div>
        </form>
          </div>

          {/* Right Sidebar - Vertical Tab Navigation */}
          <div className="w-44 bg-gray-50 p-3 rounded-br-lg">
            <div className="sticky top-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3 px-1">Navigation</h3>
              <VerticalTabNavigation tabs={bookingTabs} activeTab={activeTab} setActiveTab={setActiveTab} />
              
              {/* Quick Actions */}
              <div className="mt-6 pt-4 border-t">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3 px-1">Quick Actions</h3>
                <div className="flex flex-col gap-2">
                  {/* Generate Proforma Invoice */}
                  {formData.brand_name && (
                    <button
                      type="button"
                      onClick={() => navigate(`/sales/proforma-invoice/booking/${id}`)}
                      className="px-3 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-1.5"
                      data-testid="quick-proforma-btn"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      Proforma Invoice
                    </button>
                  )}
                  {!isViewMode && (
                    <button
                      type="button"
                      onClick={() => document.querySelector('[data-testid="submit-booking-btn"]')?.click()}
                      className="px-3 py-2 text-sm text-white rounded-xl font-medium shadow-[0_4px_12px_rgba(79,70,229,0.30)] hover:shadow-[0_8px_20px_rgba(79,70,229,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95" style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #6D28D9 100%)' }}
                    >
                      Save
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={navigateBack}
                    className="px-3 py-2 text-sm border rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    Back to List
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Template Selection Modal */}
      {showTemplateModal && (
        <TemplateSelectionModal
          templateType={templateType}
          templates={templates}
          selectedTemplate={selectedTemplate}
          onTemplateSelect={handleTemplateSelect}
          messagePreview={messagePreview}
          replacePlaceholders={replacePlaceholders}
          formData={formData}
          onClose={() => setShowTemplateModal(false)}
          onSend={sendMessage}
        />
      )}

      {/* Elegant Chat-Style Notification Prompt */}
      {showNotificationPrompt && (
        <NotificationPromptModal
          notificationTrigger={notificationTrigger}
          formData={formData}
          onChoice={handleNotificationChoice}
        />
      )}
    </SalesLayout>
  );
}

export default BookingForm;
